import React, { useEffect, useRef, useState } from 'react';
import { Box, styled, Typography } from '@mui/material';

/** Live preview screens, in flow order. `qr` renders in the desktop frame. */
export const PREVIEW_STEPS = [
  'qr',
  'terms',
  'selection',
  'capture',
  'progress',
  'retry',
  'error',
  'cancel',
  'success',
  'review',
  'fail',
] as const;
export type PreviewStepId = (typeof PREVIEW_STEPS)[number];

export const PREVIEW_STEP_LABELS: Record<PreviewStepId, string> = {
  qr: 'QR handoff',
  terms: 'Terms',
  selection: 'Selection',
  capture: 'Capture',
  progress: 'Progress',
  retry: 'Retry',
  error: 'Error',
  cancel: 'Cancellation',
  success: 'Success',
  review: 'Needs review',
  fail: 'Failed',
};

export type PreviewFlowKind = 'poi' | 'poa';

export type PreviewDocumentRow = {
  key: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
};

/** Resolved colors/fonts the preview screens render with (from the design form). */
export type PreviewTheme = {
  fontFamily?: string;
  screenBg: string;
  cardBg: string;
  text: string;
  headerBg: string;
  headerText: string;
  buttonBg: string;
  buttonText: string;
  link: string;
  error: string;
  accent: string;
  toggle: string;
  docSelectBg: string;
  docText: string;
  radio: string;
  reasonActiveBg: string;
};

export type PreviewTermsCopy = {
  title: string;
  consentLabel: string;
  consentMessage: string;
  moreDetails: string;
  privacyPolicy: string;
  and: string;
  termsOfService: string;
  acceptToContinue: string;
  startVerification: string;
};

type ScreenProps = {
  step: PreviewStepId;
  onStepChange: (step: PreviewStepId) => void;
  kind: PreviewFlowKind;
  theme: PreviewTheme;
  documents: PreviewDocumentRow[];
  terms: PreviewTermsCopy;
  cancelText: string;
  logoSrc?: string | null;
  bannerSrc?: string | null;
  showProgressBar: boolean;
};

const SUCCESS_GREEN = '#43A15B';
const WARNING_AMBER = '#E3A72F';
const OUTLINE_BORDER = '#D0D5DD';

/** Phone shell matching the card-scanner console live preview (340px wide, 640px screen). */
const PhoneFrame = styled(Box)({
  width: '340px',
  maxWidth: '100%',
  boxSizing: 'border-box',
  border: '4px solid #171717',
  borderRadius: '32px',
  background: '#000',
  overflow: 'hidden',
  boxShadow:
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
});

const PhoneScreen = styled(Box)({
  height: '640px',
  width: '100%',
  overflowY: 'auto',
  position: 'relative',
});

/** Desktop shell matching the console: 900px max, 3:2, content laid out at 1440x960 and scaled. */
const DESKTOP_WIDTH = 1440;
const DESKTOP_HEIGHT = 960;

function DesktopFrame({ children }: { children: React.ReactNode }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.375);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) =>
      setScale(entry.contentRect.width / DESKTOP_WIDTH),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={frameRef}
      sx={{
        width: '100%',
        maxWidth: 900,
        aspectRatio: '3 / 2',
        overflow: 'hidden',
        borderRadius: '12px',
        border: '1px solid #E5E5E5',
        background: '#fff',
        boxShadow:
          '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Box
        sx={{
          width: DESKTOP_WIDTH,
          height: DESKTOP_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export function FlowPreview(props: ScreenProps) {
  if (props.step === 'qr') {
    return (
      <DesktopFrame>
        <QrScreen {...props} />
      </DesktopFrame>
    );
  }
  return (
    <PhoneFrame>
      <PhoneScreen
        sx={{
          backgroundColor: props.theme.screenBg,
          fontFamily: props.theme.fontFamily,
          color: props.theme.text,
        }}
      >
        <PhoneScreenContent {...props} />
      </PhoneScreen>
    </PhoneFrame>
  );
}

/* ------------------------------------------------------------------ */
/* Shared pieces                                                       */
/* ------------------------------------------------------------------ */

const muted = (theme: PreviewTheme) => ({ color: theme.text, opacity: 0.7 });

function CloseIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function StatusBar({ color }: { color: string }) {
  return (
    <Box
      sx={{
        height: 36,
        px: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '13px',
        fontWeight: 600,
        color,
      }}
    >
      <span>9:41</span>
      <svg width="54" height="11" viewBox="0 0 54 11" fill={color}>
        <rect x="0" y="7" width="3" height="4" rx="0.5" />
        <rect x="4.5" y="5" width="3" height="6" rx="0.5" />
        <rect x="9" y="3" width="3" height="8" rx="0.5" />
        <rect x="13.5" y="1" width="3" height="10" rx="0.5" />
        <path d="M27 2.5c2 0 3.8.8 5.2 2l1-1A8.8 8.8 0 0 0 27 1a8.8 8.8 0 0 0-6.2 2.5l1 1c1.4-1.2 3.2-2 5.2-2zm0 3c1.2 0 2.2.4 3 1.1l1-1A5.8 5.8 0 0 0 27 4a5.8 5.8 0 0 0-4 1.6l1 1c.8-.7 1.8-1.1 3-1.1zm0 3c-.5 0-1 .2-1.3.5L27 10.3l1.3-1.3c-.3-.3-.8-.5-1.3-.5z" />
        <rect
          x="36.5"
          y="1"
          width="15"
          height="9"
          rx="2"
          fill="none"
          stroke={color}
        />
        <rect x="38" y="2.5" width="12" height="6" rx="1" />
        <rect x="52.5" y="4" width="1.5" height="3" rx="0.5" />
      </svg>
    </Box>
  );
}

function PhoneHeader({
  theme,
  title,
  logoSrc,
  onBack,
  onClose,
}: {
  theme: PreviewTheme;
  title?: string;
  logoSrc?: string | null;
  onBack?: () => void;
  onClose?: () => void;
}) {
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: theme.headerBg,
        color: theme.headerText,
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      <StatusBar color={theme.headerText} />
      <Box
        sx={{
          height: 40,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: '44px',
        }}
      >
        {onBack && (
          <Box
            component="button"
            type="button"
            aria-label="Back"
            onClick={onBack}
            sx={headerIconSx({ left: 12 })}
          >
            <BackIcon />
          </Box>
        )}
        {title &&
          (logoSrc ? (
            <img
              src={logoSrc}
              alt="Logo"
              style={{ height: 22, maxWidth: 160, objectFit: 'contain' }}
            />
          ) : (
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 500,
                color: 'inherit',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
          ))}
        {onClose && (
          <Box
            component="button"
            type="button"
            aria-label="Close"
            onClick={onClose}
            sx={headerIconSx({ right: 12 })}
          >
            <CloseIcon />
          </Box>
        )}
      </Box>
    </Box>
  );
}

const headerIconSx = (pos: { left?: number; right?: number }) => ({
  position: 'absolute' as const,
  ...pos,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  p: '4px',
  border: 'none',
  background: 'none',
  color: 'inherit',
  cursor: 'pointer',
});

function PrimaryButton({
  theme,
  children,
  onClick,
}: {
  theme: PreviewTheme;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width: '100%',
        height: 40,
        border: 'none',
        borderRadius: '6px',
        backgroundColor: theme.buttonBg,
        color: theme.buttonText,
        fontFamily: 'inherit',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {children}
    </Box>
  );
}

function OutlineButton({
  theme,
  children,
  onClick,
  filled = false,
}: {
  theme: PreviewTheme;
  children: React.ReactNode;
  onClick?: () => void;
  /** Grey-filled variant used for the cancel confirmation. */
  filled?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width: '100%',
        height: 40,
        borderRadius: '6px',
        border: filled ? 'none' : `1px solid ${OUTLINE_BORDER}`,
        backgroundColor: filled ? '#E9ECEF' : theme.cardBg,
        color: theme.text,
        fontFamily: 'inherit',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {children}
    </Box>
  );
}

/** Pins buttons to the bottom of the screen, below scrollable content. */
function Footer({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        mt: 'auto',
        p: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      {children}
    </Box>
  );
}

function Body({
  children,
  center = false,
}: {
  children: React.ReactNode;
  center?: boolean;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        ...(center ? { alignItems: 'center', textAlign: 'center' } : {}),
      }}
    >
      {children}
    </Box>
  );
}

function CheckBadge({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40">
      <circle cx="20" cy="20" r="20" fill={SUCCESS_GREEN} />
      <path
        d="M12 20.5l5.5 5.5L28.5 15"
        fill="none"
        stroke="#fff"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WarningTriangle({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size * 0.88} viewBox="0 0 48 42">
      <path
        d="M21.4 2.5a3 3 0 0 1 5.2 0l20 34.5a3 3 0 0 1-2.6 4.5H4a3 3 0 0 1-2.6-4.5z"
        fill={WARNING_AMBER}
      />
      <rect x="22.6" y="13" width="2.8" height="14" rx="1.4" fill="#fff" />
      <circle cx="24" cy="32.5" r="1.8" fill="#fff" />
    </svg>
  );
}

function ListMark({ ok }: { ok: boolean }) {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" style={{ flexShrink: 0 }}>
      <circle cx="6" cy="6" r="6" fill={ok ? SUCCESS_GREEN : '#D0402B'} />
      {ok ? (
        <path
          d="M3.4 6.2l1.8 1.8 3.4-3.6"
          fill="none"
          stroke="#fff"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M4 4l4 4M8 4l-4 4"
          stroke="#fff"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

function ProgressBar({
  value,
  color,
  track = '#D9D9D9',
  height = 4,
}: {
  value: number;
  color: string;
  track?: string;
  height?: number;
}) {
  return (
    <Box
      sx={{
        height,
        borderRadius: height,
        backgroundColor: track,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: `${value}%`,
          height: '100%',
          borderRadius: height,
          backgroundColor: color,
          transition: 'width 0.3s ease',
        }}
      />
    </Box>
  );
}

/** Stylised document used in capture/progress screens (no real ID imagery). */
function DocumentArt({
  kind,
  border,
}: {
  kind: PreviewFlowKind;
  border?: string;
}) {
  if (kind === 'poa') {
    return (
      <Box
        sx={{
          width: 120,
          height: 160,
          borderRadius: '10px',
          border: `2px solid ${border ?? '#3A3A3A'}`,
          backgroundColor: '#ECECEC',
          p: '14px',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '4px',
            backgroundColor: '#8A8A8A',
          }}
        />
        {[80, 66, 80, 50].map((w, i) => (
          <Box
            key={i}
            sx={{
              mt: i === 0 ? '18px' : '7px',
              height: 4,
              width: `${w}%`,
              borderRadius: 2,
              backgroundColor: '#C2C2C2',
            }}
          />
        ))}
        <Box sx={{ display: 'flex', gap: '4px', mt: '18px' }}>
          {[0, 1, 2, 3].map(i => (
            <Box
              key={i}
              sx={{
                width: 12,
                height: 4,
                borderRadius: 2,
                backgroundColor: '#C2C2C2',
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 210,
        height: 132,
        borderRadius: '10px',
        border: `3px solid ${border ?? '#fff'}`,
        backgroundColor: '#C9DDF5',
        p: '12px',
        boxSizing: 'border-box',
        display: 'flex',
        gap: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
      }}
    >
      <Box
        sx={{
          width: 58,
          height: 72,
          mt: '14px',
          borderRadius: '4px',
          backgroundColor: '#8FA9C8',
        }}
      />
      <Box sx={{ flex: 1, pt: '4px' }}>
        <Box
          sx={{
            height: 8,
            width: '70%',
            borderRadius: 2,
            backgroundColor: '#5A7DA8',
          }}
        />
        {[90, 70, 85, 60, 75].map((w, i) => (
          <Box
            key={i}
            sx={{
              mt: '8px',
              height: 4,
              width: `${w}%`,
              borderRadius: 2,
              backgroundColor: '#9DB6D3',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Phone screens                                                       */
/* ------------------------------------------------------------------ */

function PhoneScreenContent(props: ScreenProps) {
  const { step, onStepChange, kind, theme, logoSrc } = props;
  const title =
    kind === 'poa' ? 'Address verification' : 'Identity verification';
  const toCancel = () => onStepChange('cancel');

  const header = (() => {
    switch (step) {
      case 'terms':
        return (
          <PhoneHeader
            theme={theme}
            title={title}
            logoSrc={logoSrc}
            onClose={toCancel}
          />
        );
      case 'selection':
        return (
          <PhoneHeader
            theme={theme}
            title={title}
            logoSrc={logoSrc}
            onBack={() => onStepChange('terms')}
            onClose={toCancel}
          />
        );
      case 'capture':
      case 'progress':
        return (
          <PhoneHeader
            theme={theme}
            title={title}
            logoSrc={logoSrc}
            onBack={() => onStepChange('selection')}
            onClose={toCancel}
          />
        );
      case 'cancel':
        return <PhoneHeader theme={theme} />;
      default:
        return <PhoneHeader theme={theme} onClose={toCancel} />;
    }
  })();

  return (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      {header}
      {props.showProgressBar &&
        (step === 'selection' || step === 'capture') && (
          <ProgressBar
            value={step === 'selection' ? 33 : 66}
            color={theme.accent}
            track="#E0E0E0"
            height={3}
          />
        )}
      {step === 'terms' && <TermsScreen {...props} />}
      {step === 'selection' && <SelectionScreen {...props} />}
      {step === 'capture' && <CaptureScreen {...props} />}
      {step === 'progress' && <ProgressScreen {...props} />}
      {step === 'retry' && <RetryScreen {...props} />}
      {step === 'error' && <ErrorScreen {...props} />}
      {step === 'cancel' && <CancelScreen {...props} />}
      {step === 'success' && (
        <EndScreen
          theme={theme}
          icon={<CheckBadge />}
          title="You are all done"
          message="You can now return to the desktop"
        />
      )}
      {step === 'review' && (
        <EndScreen
          theme={theme}
          icon={<CheckBadge />}
          title="You are all done"
          message="Your documents have been sent. They will be reviewed soon"
        />
      )}
      {step === 'fail' && (
        <EndScreen
          theme={theme}
          icon={<WarningTriangle />}
          title="Verification failed"
          message="Give it another try and restart the verification. You'll now be redirected to the home screen."
        />
      )}
    </Box>
  );
}

function TermsScreen({
  theme,
  terms,
  cancelText,
  bannerSrc,
  onStepChange,
}: ScreenProps) {
  const [consent, setConsent] = useState(true);
  const [showError, setShowError] = useState(false);

  const start = () => {
    if (!consent) {
      setShowError(true);
      return;
    }
    onStepChange('selection');
  };

  return (
    <>
      <Body>
        <Box sx={{ p: '20px 16px 0' }}>
          {bannerSrc && (
            <img
              src={bannerSrc}
              alt="Banner"
              style={{
                width: '100%',
                maxHeight: 80,
                objectFit: 'cover',
                borderRadius: 8,
                marginBottom: 16,
              }}
            />
          )}
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 600,
              mb: '12px',
              color: theme.text,
              fontFamily: 'inherit',
            }}
          >
            {terms.title}
          </Typography>
          <Box
            sx={{
              backgroundColor: theme.cardBg,
              borderRadius: '8px',
              p: '8px',
            }}
          >
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: `1px solid ${showError ? theme.error : '#E4E7EC'}`,
                borderRadius: '6px',
                p: '6px 8px',
                cursor: 'pointer',
              }}
            >
              <Typography
                sx={{ fontSize: '12px', color: theme.text, fontFamily: 'inherit' }}
              >
                {terms.consentLabel}
              </Typography>
              <Toggle
                on={consent}
                color={theme.toggle}
                onChange={v => {
                  setConsent(v);
                  if (v) setShowError(false);
                }}
              />
            </Box>
            {showError && (
              <Typography
                sx={{
                  fontSize: '10.5px',
                  color: theme.error,
                  mt: '4px',
                  fontFamily: 'inherit',
                }}
              >
                {terms.acceptToContinue}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: '11.5px',
                lineHeight: 1.45,
                mt: '8px',
                color: theme.text,
                fontFamily: 'inherit',
              }}
            >
              {terms.consentMessage}
            </Typography>
            <Typography
              sx={{
                fontSize: '11.5px',
                lineHeight: 1.45,
                mt: '10px',
                mb: '4px',
                color: theme.text,
                fontFamily: 'inherit',
              }}
            >
              {terms.moreDetails}
              <Box
                component="span"
                sx={{ color: theme.link, textDecoration: 'underline' }}
              >
                {terms.privacyPolicy}
              </Box>{' '}
              {terms.and}{' '}
              <Box
                component="span"
                sx={{ color: theme.link, textDecoration: 'underline' }}
              >
                {terms.termsOfService}
              </Box>
            </Typography>
          </Box>
        </Box>
      </Body>
      <Footer>
        <PrimaryButton theme={theme} onClick={start}>
          {terms.startVerification}
        </PrimaryButton>
        <OutlineButton theme={theme} onClick={() => onStepChange('cancel')}>
          {cancelText}
        </OutlineButton>
      </Footer>
    </>
  );
}

function Toggle({
  on,
  color,
  onChange,
}: {
  on: boolean;
  color: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      sx={{
        width: 30,
        height: 17,
        p: '2px',
        border: on ? 'none' : `1.5px solid ${color}`,
        borderRadius: 17,
        backgroundColor: on ? color : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: on ? 'flex-end' : 'flex-start',
        cursor: 'pointer',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: on ? 13 : 10,
          height: on ? 13 : 10,
          borderRadius: '50%',
          backgroundColor: on ? '#fff' : color,
        }}
      />
    </Box>
  );
}

function SelectionScreen({
  theme,
  kind,
  documents,
  onStepChange,
}: ScreenProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    if (selected && !documents.some(d => d.key === selected)) {
      setSelected(null);
    }
  }, [documents, selected]);

  return (
    <>
      <Body>
        <Box sx={{ p: '16px 12px 0' }}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: theme.text,
              fontFamily: 'inherit',
              mb: kind === 'poa' ? '6px' : '12px',
            }}
          >
            {kind === 'poa'
              ? 'Which document would you like to use?'
              : 'Which document do you want to use?'}
          </Typography>
          {kind === 'poa' && (
            <Typography
              sx={{
                fontSize: '12.5px',
                lineHeight: 1.4,
                color: theme.text,
                fontFamily: 'inherit',
                mb: '12px',
              }}
            >
              The document must show your <b>full name</b>,{' '}
              <b>residential address</b> and must{' '}
              <b>not be older than 3 months</b>
            </Typography>
          )}

          {documents.length === 0 ? (
            <Typography
              sx={{ fontSize: '12px', py: 2, fontFamily: 'inherit', ...muted(theme) }}
            >
              No document types selected. Enable them under Basic settings →
              Document types.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {documents.map(doc => {
                const isSelected = doc.key === selected;
                return (
                  <Box
                    key={doc.key}
                    component="button"
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setSelected(isSelected ? null : doc.key)}
                    sx={{
                      display: 'flex',
                      alignItems: doc.description ? 'flex-start' : 'center',
                      gap: '10px',
                      width: '100%',
                      textAlign: 'left',
                      p: '12px',
                      borderRadius: '6px',
                      border: 'none',
                      outline: `2px solid ${
                        isSelected ? theme.link : 'transparent'
                      }`,
                      outlineOffset: '2px',
                      backgroundColor: theme.docSelectBg,
                      color: theme.docText,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s',
                    }}
                  >
                    <Box sx={{ flexShrink: 0, display: 'flex' }}>{doc.icon}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '12.5px',
                          fontWeight: 600,
                          color: 'inherit',
                          fontFamily: 'inherit',
                        }}
                      >
                        {doc.label}
                      </Typography>
                      {doc.description && (
                        <Typography
                          sx={{
                            fontSize: '10.5px',
                            lineHeight: 1.35,
                            mt: '2px',
                            color: 'inherit',
                            opacity: 0.8,
                            fontFamily: 'inherit',
                          }}
                        >
                          {doc.description}
                        </Typography>
                      )}
                    </Box>
                    {kind === 'poi' && (
                      <Box
                        component="span"
                        sx={{ opacity: 0.5, fontSize: '16px', lineHeight: 1 }}
                      >
                        ›
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Body>

      {selected && (
        <Footer>
          <PrimaryButton theme={theme} onClick={() => onStepChange('capture')}>
            Take a picture
          </PrimaryButton>
          <OutlineButton theme={theme} onClick={() => setSheetOpen(true)}>
            Upload
          </OutlineButton>
        </Footer>
      )}

      {sheetOpen && (
        <UploadSheet
          onPick={() => {
            setSheetOpen(false);
            onStepChange('progress');
          }}
          onCamera={() => {
            setSheetOpen(false);
            onStepChange('capture');
          }}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </>
  );
}

/** iOS-style action sheet shown after tapping Upload. */
function UploadSheet({
  onPick,
  onCamera,
  onClose,
}: {
  onPick: () => void;
  onCamera: () => void;
  onClose: () => void;
}) {
  const rowSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    p: '11px 14px',
    border: 'none',
    background: 'none',
    fontSize: '14px',
    color: '#111',
    cursor: 'pointer',
    fontFamily: '-apple-system, system-ui, sans-serif',
    '&:not(:last-of-type)': { borderBottom: '0.5px solid #D1D1D6' },
  };
  return (
    <Box
      onClick={onClose}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        backgroundColor: 'rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        p: '8px',
        gap: '8px',
      }}
    >
      <Box
        onClick={e => e.stopPropagation()}
        sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#F9F9F9',
        }}
      >
        <Box component="button" type="button" onClick={onCamera} sx={rowSx}>
          Take Photo or Video <SheetIcon d="M4 8h3l2-2h6l2 2h3v11H4z" />
        </Box>
        <Box component="button" type="button" onClick={onPick} sx={rowSx}>
          Photo Library <SheetIcon d="M4 6h16v12H4zM4 15l5-5 4 4 3-3 4 4" />
        </Box>
        <Box component="button" type="button" onClick={onPick} sx={rowSx}>
          Browse <SheetIcon d="M6 6h.01M12 6h.01M18 6h.01" />
        </Box>
      </Box>
      <Box
        component="button"
        type="button"
        onClick={onClose}
        sx={{
          height: 44,
          border: 'none',
          borderRadius: '12px',
          backgroundColor: '#fff',
          color: '#007AFF',
          fontSize: '15px',
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: '-apple-system, system-ui, sans-serif',
        }}
      >
        Cancel
      </Box>
    </Box>
  );
}

function SheetIcon({ d }: { d: string }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#007AFF"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={d} />
    </svg>
  );
}

function CaptureScreen({ kind, onStepChange }: ScreenProps) {
  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: '#4A4A4A',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: '20px 16px',
      }}
    >
      <Box
        sx={{
          px: '18px',
          py: '7px',
          borderRadius: 20,
          backgroundColor: '#111',
          color: '#fff',
          fontSize: '12px',
          fontWeight: 600,
        }}
      >
        {kind === 'poa' ? 'Fit your document in the frame' : 'Front of your document'}
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DocumentArt kind={kind} border="#fff" />
      </Box>
      <Box
        component="button"
        type="button"
        aria-label="Capture"
        onClick={() => onStepChange('progress')}
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: '4px solid rgba(255,255,255,0.6)',
          backgroundColor: '#fff',
          backgroundClip: 'padding-box',
          cursor: 'pointer',
        }}
      />
    </Box>
  );
}

function ProgressScreen({ kind, theme }: ScreenProps) {
  if (kind === 'poa') {
    return (
      <Body center>
        <Box sx={{ p: '24px 20px 0', width: '100%', boxSizing: 'border-box' }}>
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 500,
              color: theme.text,
              fontFamily: 'inherit',
            }}
          >
            Scanning your document
          </Typography>
          <Typography
            sx={{
              fontSize: '11px',
              lineHeight: 1.4,
              mt: '6px',
              fontFamily: 'inherit',
              ...muted(theme),
            }}
          >
            We're processing your documents. This might take a short moment.
          </Typography>
        </Box>
        <Box
          sx={{
            position: 'relative',
            mt: '28px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <DocumentArt kind="poa" />
          <Box
            sx={{
              position: 'absolute',
              left: -16,
              right: -16,
              top: 0,
              height: 2,
              backgroundColor: '#3A3A3A',
              opacity: 0.6,
              animation: 'previewScan 2.4s ease-in-out infinite alternate',
              '@keyframes previewScan': {
                from: { top: 0 },
                to: { top: 158 },
              },
            }}
          />
        </Box>
        <Box sx={{ mt: 'auto', p: '16px 20px 32px', width: '100%', boxSizing: 'border-box' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '10.5px',
              mb: '6px',
              color: theme.text,
            }}
          >
            <span>Uploading</span>
            <span>4%</span>
          </Box>
          <ProgressBar value={4} color={theme.error} />
        </Box>
      </Body>
    );
  }
  return (
    <Body center>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <DocumentArt kind="poi" />
      </Box>
      <Box sx={{ width: 180, pb: '48px' }}>
        <Typography
          sx={{
            fontSize: '12.5px',
            fontWeight: 600,
            mb: '8px',
            color: theme.text,
            fontFamily: 'inherit',
          }}
        >
          Inspecting ...
        </Typography>
        <ProgressBar value={75} color={SUCCESS_GREEN} />
      </Box>
    </Body>
  );
}

function RetryScreen({ kind, theme, documents, onStepChange }: ScreenProps) {
  return (
    <>
      <Body>
        <Box
          sx={{
            p: '24px 16px 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <WarningTriangle />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              mt: '8px',
              color: theme.text,
              fontFamily: 'inherit',
            }}
          >
            {kind === 'poa'
              ? 'Document not accepted'
              : "The document isn't supported"}
          </Typography>
        </Box>
        <Box sx={{ p: '20px 16px 0', fontSize: '12px', color: theme.text }}>
          {kind === 'poa' ? (
            <>
              <Typography sx={retryHeadingSx}>Document requirements:</Typography>
              {[
                'Shows your name',
                'Shows your address',
                'Shows issue date (not older than 6 months)',
                'Shows the issuer logo',
              ].map(t => (
                <RetryItem key={t} ok text={t} />
              ))}
              <Typography sx={{ ...retryHeadingSx, mt: '10px' }}>
                We don't accept:
              </Typography>
              {[
                'Payslip',
                'Insurance',
                'Mobile phone bills',
                'Invoices from online shops',
              ].map(t => (
                <RetryItem key={t} ok={false} text={t} />
              ))}
            </>
          ) : (
            <>
              <Typography sx={retryHeadingSx}>
                Please try again with one of the following documents:
              </Typography>
              {documents.map(d => (
                <RetryItem key={d.key} ok text={d.label} />
              ))}
            </>
          )}
        </Box>
      </Body>
      <Footer>
        <PrimaryButton theme={theme} onClick={() => onStepChange('selection')}>
          Try again
        </PrimaryButton>
      </Footer>
    </>
  );
}

const retryHeadingSx = {
  fontSize: '12px',
  mb: '4px',
  color: 'inherit',
  fontFamily: 'inherit',
};

function RetryItem({ ok, text }: { ok: boolean; text: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        py: '2px',
        fontSize: '12px',
      }}
    >
      <ListMark ok={ok} />
      {text}
    </Box>
  );
}

function ErrorScreen({ theme, onStepChange }: ScreenProps) {
  return (
    <>
      <Body center>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pb: '40px',
          }}
        >
          <WarningTriangle />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              mt: '8px',
              color: theme.text,
              fontFamily: 'inherit',
            }}
          >
            Something went wrong...
          </Typography>
          <Typography
            sx={{ fontSize: '12px', mt: '2px', fontFamily: 'inherit', ...muted(theme) }}
          >
            Please try again.
          </Typography>
        </Box>
      </Body>
      <Footer>
        <PrimaryButton theme={theme} onClick={() => onStepChange('terms')}>
          Try again
        </PrimaryButton>
      </Footer>
    </>
  );
}

const CANCEL_REASONS = [
  "Don't have my document",
  'Document no longer valid',
  "Don't have time right now",
  'Privacy concerns',
  'Incorrect details',
  'Technical issues',
  'Camera access denied',
  'Other',
];

function CancelScreen({ theme, cancelText, onStepChange }: ScreenProps) {
  const [reason, setReason] = useState<string | null>(null);
  return (
    <>
      <Body>
        <Box sx={{ p: '20px 12px 0' }}>
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 500,
              color: theme.text,
              fontFamily: 'inherit',
            }}
          >
            Cancel?
          </Typography>
          <Typography
            sx={{ fontSize: '11.5px', mt: '2px', mb: '12px', fontFamily: 'inherit', ...muted(theme) }}
          >
            Pick a reason or verify later.
          </Typography>
          <Box
            role="radiogroup"
            sx={{
              backgroundColor: theme.cardBg,
              borderRadius: '8px',
              py: '6px',
            }}
          >
            {CANCEL_REASONS.map(r => {
              const active = r === reason;
              return (
                <Box
                  key={r}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setReason(r)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    p: '8px 16px',
                    border: 'none',
                    backgroundColor: active ? theme.reasonActiveBg : 'transparent',
                    color: theme.text,
                    fontFamily: 'inherit',
                    fontSize: '11.5px',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  {r}
                  <Box
                    sx={{
                      width: 13,
                      height: 13,
                      borderRadius: '50%',
                      boxSizing: 'border-box',
                      border: active
                        ? `4px solid ${theme.radio}`
                        : '1px solid #98A2B3',
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Body>
      <Box
        sx={{
          mt: 'auto',
          p: '12px 8px 16px',
          backgroundColor: theme.cardBg,
          borderRadius: '12px 12px 0 0',
          boxShadow: '0 -1px 4px rgba(0,0,0,0.06)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          position: 'sticky',
          bottom: 0,
        }}
      >
        <OutlineButton theme={theme} filled onClick={() => onStepChange('fail')}>
          {cancelText}
        </OutlineButton>
        <OutlineButton theme={theme} onClick={() => onStepChange('terms')}>
          Continue
        </OutlineButton>
      </Box>
    </>
  );
}

function EndScreen({
  theme,
  icon,
  title,
  message,
}: {
  theme: PreviewTheme;
  icon: React.ReactNode;
  title: string;
  message: string;
}) {
  return (
    <Body center>
      <Box sx={{ pt: '96px', px: '28px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>{icon}</Box>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            mt: '10px',
            color: theme.text,
            fontFamily: 'inherit',
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: '11px',
            lineHeight: 1.4,
            mt: '2px',
            fontFamily: 'inherit',
            ...muted(theme),
          }}
        >
          {message}
        </Typography>
      </Box>
    </Body>
  );
}

/* ------------------------------------------------------------------ */
/* Desktop QR handoff                                                  */
/* ------------------------------------------------------------------ */

function QrScreen({ theme, logoSrc, onStepChange }: ScreenProps) {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.screenBg,
        fontFamily: theme.fontFamily,
        color: theme.text,
      }}
    >
      <Box
        sx={{
          height: 56,
          flexShrink: 0,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.headerBg,
          color: theme.headerText,
        }}
      >
        {logoSrc ? (
          <img
            src={logoSrc}
            alt="Logo"
            style={{ height: 28, maxWidth: 220, objectFit: 'contain' }}
          />
        ) : (
          <Typography sx={{ fontSize: 18, fontWeight: 600, fontFamily: 'inherit' }}>
            LOGO
          </Typography>
        )}
        <Box sx={{ position: 'absolute', right: 24, display: 'flex' }}>
          <CloseIcon size={24} />
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '96px',
          px: '120px',
        }}
      >
        <Box sx={{ maxWidth: 560, fontSize: 18, lineHeight: 1.5 }}>
          <Typography
            sx={{
              fontSize: 36,
              fontWeight: 600,
              lineHeight: 1.2,
              color: theme.text,
              fontFamily: 'inherit',
            }}
          >
            Continue your verification using your mobile
          </Typography>
          <Typography sx={{ fontSize: 18, mt: '12px', fontFamily: 'inherit', color: theme.text }}>
            It's easier with your smartphone and you don't have to download an
            extra app.
          </Typography>
          <Box
            component="ol"
            sx={{ pl: '24px', mt: '28px', mb: 0, listStyle: 'decimal' }}
          >
            <li>Scan the QR code and open the link</li>
            <Box component="li" sx={{ mt: '10px' }}>
              Follow the instructions
            </Box>
            <Box component="li" sx={{ mt: '10px' }}>
              That's it
              <Box sx={{ fontSize: 16, ...muted(theme) }}>
                This screen will automatically update once you successfully
                complete the verification.
              </Box>
            </Box>
          </Box>
          <Typography sx={{ fontSize: 14, mt: '28px', fontFamily: 'inherit', ...muted(theme) }}>
            You can also continue on{' '}
            <Box
              component="button"
              type="button"
              onClick={() => onStepChange('terms')}
              sx={{
                p: 0,
                border: 'none',
                background: 'none',
                color: theme.link,
                textDecoration: 'underline',
                font: 'inherit',
                cursor: 'pointer',
              }}
            >
              desktop
            </Box>
            , you need a video camera for that.
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', fontSize: 14 }}>
          <Box
            sx={{
              p: '20px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            }}
          >
            <FakeQr size={220} />
          </Box>
          <Box sx={{ mt: '12px', lineHeight: 1.4, color: theme.text }}>
            Trouble scanning the code?
            <br />
            Copy this link by{' '}
            <Box component="span" sx={{ color: theme.link, textDecoration: 'underline' }}>
              clicking here
            </Box>{' '}
            and open it on your mobile
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/** Deterministic QR-like pattern (decorative only; not scannable). */
function FakeQr({ size }: { size: number }) {
  const n = 25;
  const cells: React.ReactNode[] = [];
  let seed = 7;
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  const inFinder = (x: number, y: number) =>
    (x < 8 && y < 8) || (x >= n - 8 && y < 8) || (x < 8 && y >= n - 8);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (!inFinder(x, y) && rand() > 0.5) {
        cells.push(<rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} />);
      }
    }
  }
  const finder = (x: number, y: number) => (
    <g key={`f-${x}-${y}`}>
      <rect x={x} y={y} width={7} height={7} />
      <rect x={x + 1} y={y + 1} width={5} height={5} fill="#fff" />
      <rect x={x + 2} y={y + 2} width={3} height={3} />
    </g>
  );
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${n} ${n}`}
      shapeRendering="crispEdges"
      fill="#000"
    >
      {cells}
      {finder(0, 0)}
      {finder(n - 7, 0)}
      {finder(0, n - 7)}
    </svg>
  );
}
